// src/components/EditorCliente.js
//
// Dialog con tabs para crear/editar un cliente.
// Tabs:
//   1. Identificación: tipo persona, documento, nombres/razón social, grupo.
//   2. Contacto: teléfonos, email, dirección, barrio, detalles de entrega.
//   3. Crédito y precios: crédito habilitado, límite, días, lista de precios,
//      descuento default.
//   4. Preferencias: fecha nac., género, aceptaciones de marketing, notas.

import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Tabs, Tab, Grid,
  TextField, MenuItem, Button, Alert, Stack, Chip, Switch, FormControlLabel,
  InputAdornment, Typography, IconButton, Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon  from '@mui/icons-material/Save';
import { clientes as apiClientes, gruposCliente as apiGrupos, listasPrecios as apiLP } from '../api/posApi';

const TabPanel = ({ children, value, index }) => (
  <Box hidden={value !== index} sx={{ pt: 3 }}>{value === index && children}</Box>
);

const emptyForm = {
  codigo: '', tipoPersona: 'NATURAL', tipoDocIdentidadId: null,
  numeroDocumento: '', digitoVerificacion: '',
  primerNombre: '', segundoNombre: '', primerApellido: '', segundoApellido: '',
  razonSocial: '', nombreComercial: '',
  grupoClienteId: null, regimentTributarioId: null,
  esResponsableIVA: false, esGranContribuyente: false, esAutoRetenedor: false,
  direccion: '', barrio: '', detalleEntrega: '', observacionesEntrega: '',
  municipioID: null, departamentoID: null, paisID: null,
  latitud: null, longitud: null,
  telefono: '', celular: '', email: '',
  clasificacion: 'REGULAR',
  tieneCredito: false, limiteCredito: 0, diasCredito: 0,
  listaPrecioId: null, descuentoDefault: 0,
  fechaNacimiento: '', genero: '',
  aceptaMarketing: false, aceptaSMS: false, aceptaWhatsApp: false,
  fotoUrl: '', notas: '', activo: true,
};

const EditorCliente = ({ modo, cliente, empresaId, onClose, onSaved, onError }) => {
  const editando = modo === 'editar';
  const [tab, setTab] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [grupos, setGrupos] = useState([]);
  const [listas, setListas] = useState([]);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [g, l] = await Promise.all([apiGrupos.listar(empresaId), apiLP.listar(empresaId)]);
        setGrupos(Array.isArray(g) ? g.filter(x => x.activo) : []);
        setListas(Array.isArray(l) ? l.filter(x => x.activa) : []);
      } catch { /* silent */ }
    })();
  }, [empresaId]);

  useEffect(() => {
    if (!editando) { setForm(emptyForm); return; }
    if (!cliente) return;
    setForm({
      codigo:            cliente.codigo ?? '',
      tipoPersona:       cliente.tipoPersona ?? 'NATURAL',
      tipoDocIdentidadId: cliente.tipoDocIdentidadId ?? null,
      numeroDocumento:   cliente.numeroDocumento ?? '',
      digitoVerificacion: cliente.digitoVerificacion ?? '',
      primerNombre:      cliente.primerNombre ?? '',
      segundoNombre:     cliente.segundoNombre ?? '',
      primerApellido:    cliente.primerApellido ?? '',
      segundoApellido:   cliente.segundoApellido ?? '',
      razonSocial:       cliente.razonSocial ?? '',
      nombreComercial:   cliente.nombreComercial ?? '',
      grupoClienteId:    cliente.grupoClienteId ?? null,
      regimentTributarioId: cliente.regimentTributarioId ?? null,
      esResponsableIVA:  cliente.esResponsableIVA ?? false,
      esGranContribuyente: cliente.esGranContribuyente ?? false,
      esAutoRetenedor:   cliente.esAutoRetenedor ?? false,
      direccion:         cliente.direccion ?? '',
      barrio:            cliente.barrio ?? '',
      detalleEntrega:    cliente.detalleEntrega ?? '',
      observacionesEntrega: cliente.observacionesEntrega ?? '',
      municipioID:       cliente.municipioID ?? null,
      departamentoID:    cliente.departamentoID ?? null,
      paisID:            cliente.paisID ?? null,
      latitud:           cliente.latitud ?? null,
      longitud:          cliente.longitud ?? null,
      telefono:          cliente.telefono ?? '',
      celular:           cliente.celular ?? '',
      email:             cliente.email ?? '',
      clasificacion:     cliente.clasificacion ?? 'REGULAR',
      tieneCredito:      cliente.tieneCredito ?? false,
      limiteCredito:     cliente.limiteCredito ?? 0,
      diasCredito:       cliente.diasCredito ?? 0,
      listaPrecioId:     cliente.listaPrecioId ?? null,
      descuentoDefault:  cliente.descuentoDefault ?? 0,
      fechaNacimiento:   cliente.fechaNacimiento ? cliente.fechaNacimiento.slice(0, 10) : '',
      genero:            cliente.genero ?? '',
      aceptaMarketing:   cliente.aceptaMarketing ?? false,
      aceptaSMS:         cliente.aceptaSMS ?? false,
      aceptaWhatsApp:    cliente.aceptaWhatsApp ?? false,
      fotoUrl:           cliente.fotoUrl ?? '',
      notas:             cliente.notas ?? '',
      activo:            cliente.activo ?? true,
    });
  }, [editando, cliente]);

  const upd = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(s => ({ ...s, [k]: v }));
  };

  const numOrNull = (v) => (v === '' || v === null || v === undefined) ? null : Number(v);

  const puedeGuardar = () => {
    if (!form.numeroDocumento.trim()) return false;
    if (form.tipoPersona === 'JURIDICA' && !form.razonSocial.trim()) return false;
    if (form.tipoPersona === 'NATURAL' && !form.primerNombre.trim() && !form.primerApellido.trim()) return false;
    return true;
  };

  const submit = async () => {
    if (!puedeGuardar()) return;
    setGuardando(true);
    try {
      const dto = {
        codigo: form.codigo || null,
        tipoPersona: form.tipoPersona,
        tipoDocIdentidadId: form.tipoDocIdentidadId || null,
        numeroDocumento: form.numeroDocumento.trim(),
        digitoVerificacion: form.digitoVerificacion?.trim() || null,
        primerNombre: form.primerNombre?.trim() || null,
        segundoNombre: form.segundoNombre?.trim() || null,
        primerApellido: form.primerApellido?.trim() || null,
        segundoApellido: form.segundoApellido?.trim() || null,
        razonSocial: form.razonSocial?.trim() || null,
        nombreComercial: form.nombreComercial?.trim() || null,
        grupoClienteId: form.grupoClienteId || null,
        regimentTributarioId: form.regimentTributarioId || null,
        esResponsableIVA: form.esResponsableIVA,
        esGranContribuyente: form.esGranContribuyente,
        esAutoRetenedor: form.esAutoRetenedor,
        direccion: form.direccion?.trim() || null,
        barrio: form.barrio?.trim() || null,
        detalleEntrega: form.detalleEntrega?.trim() || null,
        observacionesEntrega: form.observacionesEntrega?.trim() || null,
        municipioID: form.municipioID || null,
        departamentoID: form.departamentoID || null,
        paisID: form.paisID || null,
        latitud: numOrNull(form.latitud),
        longitud: numOrNull(form.longitud),
        telefono: form.telefono?.trim() || null,
        celular: form.celular?.trim() || null,
        email: form.email?.trim() || null,
        clasificacion: form.clasificacion || 'REGULAR',
        tieneCredito: form.tieneCredito,
        limiteCredito: numOrNull(form.limiteCredito) ?? 0,
        diasCredito: numOrNull(form.diasCredito) ?? 0,
        listaPrecioId: form.listaPrecioId || null,
        descuentoDefault: numOrNull(form.descuentoDefault) ?? 0,
        fechaNacimiento: form.fechaNacimiento || null,
        genero: form.genero || null,
        aceptaMarketing: form.aceptaMarketing,
        aceptaSMS: form.aceptaSMS,
        aceptaWhatsApp: form.aceptaWhatsApp,
        fotoUrl: form.fotoUrl || null,
        notas: form.notas || null,
        activo: form.activo,
      };
      if (editando) {
        await apiClientes.actualizar(empresaId, cliente.clienteId, dto);
      } else {
        await apiClientes.crear(empresaId, dto);
      }
      onSaved();
    } catch (e) {
      onError(e.mensajeUsuario || 'No se pudo guardar.');
    } finally { setGuardando(false); }
  };

  const esCF = cliente?.esConsumidorFinal;

  return (
    <Dialog open onClose={() => !guardando && onClose()} fullWidth maxWidth="md"
            PaperProps={{ sx: { minHeight: 560 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" fontWeight={700} noWrap>
            {editando ? (esCF ? 'Consumidor Final (sistema)' : 'Editar cliente') : 'Nuevo cliente'}
          </Typography>
          {editando && form.codigo && (
            <Typography variant="caption" color="text.secondary">
              Código: {form.codigo}
            </Typography>
          )}
        </Box>
        <FormControlLabel
          control={<Switch checked={form.activo} onChange={upd('activo')} size="small" color="success"
                           disabled={esCF} />}
          label="Activo"
        />
        <IconButton onClick={onClose} disabled={guardando}><CloseIcon /></IconButton>
      </DialogTitle>
      {esCF && (
        <Alert severity="info" sx={{ mx: 3, mt: 1 }}>
          El Consumidor Final es un cliente del sistema para ventas anónimas.
          Solo puedes editar sus datos básicos, no eliminarlo ni cambiar su documento.
        </Alert>
      )}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
        <Tab label="Identificación" />
        <Tab label="Contacto" />
        <Tab label="Crédito y precios" />
        <Tab label="Preferencias" />
      </Tabs>
      <DialogContent>
        {/* IDENTIFICACIÓN */}
        <TabPanel value={tab} index={0}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField select fullWidth label="Tipo de persona"
                         value={form.tipoPersona} onChange={upd('tipoPersona')}
                         disabled={esCF}>
                <MenuItem value="NATURAL">Persona natural</MenuItem>
                <MenuItem value="JURIDICA">Persona jurídica</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Número de documento" required
                         value={form.numeroDocumento} onChange={upd('numeroDocumento')}
                         disabled={esCF} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Dígito verificación (NIT)"
                         value={form.digitoVerificacion} onChange={upd('digitoVerificacion')}
                         disabled={esCF} />
            </Grid>

            {form.tipoPersona === 'NATURAL' ? (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Primer nombre"
                             value={form.primerNombre} onChange={upd('primerNombre')}
                             disabled={esCF} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Segundo nombre"
                             value={form.segundoNombre} onChange={upd('segundoNombre')} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Primer apellido"
                             value={form.primerApellido} onChange={upd('primerApellido')} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Segundo apellido"
                             value={form.segundoApellido} onChange={upd('segundoApellido')} />
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12} sm={7}>
                  <TextField fullWidth required label="Razón social"
                             value={form.razonSocial} onChange={upd('razonSocial')} />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <TextField fullWidth label="Nombre comercial"
                             value={form.nombreComercial} onChange={upd('nombreComercial')} />
                </Grid>
              </>
            )}

            <Grid item xs={12}><Divider><Chip label="Fiscal" size="small" /></Divider></Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel control={
                <Switch checked={form.esResponsableIVA} onChange={upd('esResponsableIVA')} />
              } label="Responsable de IVA" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel control={
                <Switch checked={form.esGranContribuyente} onChange={upd('esGranContribuyente')} />
              } label="Gran contribuyente" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel control={
                <Switch checked={form.esAutoRetenedor} onChange={upd('esAutoRetenedor')} />
              } label="Autorretenedor" />
            </Grid>

            <Grid item xs={12}><Divider><Chip label="Grupo" size="small" /></Divider></Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Grupo de cliente"
                         value={form.grupoClienteId || ''} onChange={upd('grupoClienteId')}>
                <MenuItem value="">(sin grupo)</MenuItem>
                {grupos.map(g => (
                  <MenuItem key={g.grupoClienteId} value={g.grupoClienteId}>
                    {g.nombre}{g.descuentoPorcentaje ? ` (${g.descuentoPorcentaje}% desc.)` : ''}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Clasificación"
                         value={form.clasificacion} onChange={upd('clasificacion')}>
                <MenuItem value="REGULAR">Regular</MenuItem>
                <MenuItem value="VIP">VIP</MenuItem>
                <MenuItem value="EMPLEADO">Empleado</MenuItem>
                <MenuItem value="OCASIONAL">Ocasional</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </TabPanel>

        {/* CONTACTO */}
        <TabPanel value={tab} index={1}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Teléfono fijo"
                         value={form.telefono} onChange={upd('telefono')} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Celular"
                         value={form.celular} onChange={upd('celular')} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Email" type="email"
                         value={form.email} onChange={upd('email')} />
            </Grid>
            <Grid item xs={12}><Divider><Chip label="Dirección de entrega" size="small" /></Divider></Grid>
            <Grid item xs={12} sm={7}>
              <TextField fullWidth label="Dirección"
                         value={form.direccion} onChange={upd('direccion')}
                         placeholder="Calle 123 # 45-67" />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField fullWidth label="Barrio"
                         value={form.barrio} onChange={upd('barrio')}
                         placeholder="Ej. Cabecera, El Prado" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Torre / apto / casa"
                         value={form.detalleEntrega} onChange={upd('detalleEntrega')}
                         placeholder="Torre A, apto 501" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Observaciones para entrega"
                         value={form.observacionesEntrega} onChange={upd('observacionesEntrega')}
                         placeholder="Portería nocturna, tocar timbre..." />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Latitud" type="number"
                         value={form.latitud ?? ''} onChange={upd('latitud')}
                         placeholder="7.1193" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Longitud" type="number"
                         value={form.longitud ?? ''} onChange={upd('longitud')}
                         placeholder="-73.1227" />
            </Grid>
          </Grid>
        </TabPanel>

        {/* CRÉDITO Y PRECIOS */}
        <TabPanel value={tab} index={2}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControlLabel control={
                <Switch checked={form.tieneCredito} onChange={upd('tieneCredito')} />
              } label="Habilitar fiado (crédito) para este cliente" />
            </Grid>
            {form.tieneCredito && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Límite de crédito" type="number"
                             value={form.limiteCredito} onChange={upd('limiteCredito')}
                             InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                             helperText="Máximo saldo pendiente permitido. 0 = sin límite." />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Días de crédito" type="number"
                             value={form.diasCredito} onChange={upd('diasCredito')}
                             helperText="Plazo estándar de pago." />
                </Grid>
              </>
            )}
            <Grid item xs={12}><Divider><Chip label="Precios" size="small" /></Divider></Grid>
            <Grid item xs={12} sm={7}>
              <TextField select fullWidth label="Lista de precios asignada (opcional)"
                         value={form.listaPrecioId || ''} onChange={upd('listaPrecioId')}
                         helperText="Si asignas una lista, prevalece sobre la del almacén al facturarle a este cliente.">
                <MenuItem value="">(usar lista del almacén / precio base)</MenuItem>
                {listas.map(l => (
                  <MenuItem key={l.listaPrecioId} value={l.listaPrecioId}>{l.nombre}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField fullWidth label="Descuento por defecto" type="number"
                         value={form.descuentoDefault} onChange={upd('descuentoDefault')}
                         InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                         helperText="Se aplica automáticamente a cada venta." />
            </Grid>
          </Grid>
        </TabPanel>

        {/* PREFERENCIAS */}
        <TabPanel value={tab} index={3}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth type="date" label="Fecha de nacimiento"
                         InputLabelProps={{ shrink: true }}
                         value={form.fechaNacimiento} onChange={upd('fechaNacimiento')} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField select fullWidth label="Género"
                         value={form.genero} onChange={upd('genero')}>
                <MenuItem value="">(no especificar)</MenuItem>
                <MenuItem value="M">Masculino</MenuItem>
                <MenuItem value="F">Femenino</MenuItem>
                <MenuItem value="O">Otro</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}><Divider><Chip label="Comunicaciones" size="small" /></Divider></Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel control={
                <Switch checked={form.aceptaMarketing} onChange={upd('aceptaMarketing')} />
              } label="Marketing por email" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel control={
                <Switch checked={form.aceptaSMS} onChange={upd('aceptaSMS')} />
              } label="SMS" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel control={
                <Switch checked={form.aceptaWhatsApp} onChange={upd('aceptaWhatsApp')} />
              } label="WhatsApp" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={3} label="Notas internas"
                         value={form.notas} onChange={upd('notas')} />
            </Grid>
          </Grid>
        </TabPanel>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={guardando}>Cancelar</Button>
        <Button variant="contained" onClick={submit} startIcon={<SaveIcon />}
                disabled={!puedeGuardar() || guardando}>
          {guardando ? 'Guardando…' : (editando ? 'Guardar cambios' : 'Crear cliente')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditorCliente;
